/**
 * The sign-in page: one question (the work e-mail), then the step the BFF chose — the company's SSO, or a code —
 * and, after a code, the passkey step where it matters. Passkeys are also offered without asking: in the e-mail
 * field's autofill, and as "Continue" for the account this device remembers.
 */

export const login = {
  title: 'Sign in',
  subtitle: 'Smith Factory',
  identifyLead: "Enter your work e-mail — we'll take you the right way.",
  workEmail: 'Work e-mail',
  workEmailPlaceholder: 'you@company.com',
  invalidEmail: 'Enter your full e-mail address, like you@company.com.',
  firstTimeHint: 'First time here? Use the address your invitation was sent to.',
  usePasskey: 'Sign in with a passkey',
  noPasskeyHere: 'No passkey on this device? Continue with your e-mail above.',
  continueWithOrgSso: "Continue with your organization's SSO",
  signInWith: (label: string) => `Sign in with ${label}`,

  // A returning person on this device.
  welcomeBack: 'Welcome back',
  continueAs: 'Continue',
  rememberedVia: {
    passkey: 'with your passkey',
    code: 'with a code by e-mail',
    sso: (label: string) => `through ${label}`
  },
  useAnotherAccount: 'Use another account',
  forgetAccount: 'Remove from this device',
  passkeyFallback: "Didn't work? E-mail me a code instead",

  // The company's SSO.
  redirecting: (label: string) => `Taking you to ${label}…`,

  // The code.
  codeTitle: 'Check your e-mail',
  // Split so the JSX can bold the address between the halves.
  codeSentBefore: 'We sent an 8-digit code to',
  codeSentAfter: '.',
  codeArrives: 'It arrives within a minute if this address has access to the portal — check spam too.',
  verificationCode: 'Code',
  signIn: 'Sign in',
  resend: 'Send a new code',
  resendIn: (seconds: number) => `New code in ${seconds}s`,
  codeResent: 'A new code is on its way. Use the newest one.',
  useDifferentEmail: 'Use a different e-mail',
  usePasskeyInstead: 'Use a passkey instead',

  // After the code: the passkey step.
  passkeyRequiredTitle: 'Create a passkey',
  passkeyRequiredBody: (companies: string) =>
    `${companies} signs in with passkeys. Create one on this device — you'll confirm with your fingerprint, face or screen lock. No password.`,
  passkeyRequiredBodyGeneric:
    "Your company signs in with passkeys. Create one on this device — you'll confirm with your fingerprint, face or screen lock. No password.",
  passkeyRecommendedTitle: 'Add a passkey',
  passkeyAdminReason:
    "You're an administrator: managing people needs a passkey — an e-mailed code isn't enough for that.",
  passkeyCompaniesReason: (companies: string) => `A passkey also lets you into ${companies}.`,
  passkeyOptionalTitle: 'Skip the code next time',
  passkeyOptionalBody:
    'Create a passkey on this device and sign in with your fingerprint, face or screen lock — no code to wait for.',
  passkeyHowTo: 'Your browser will ask how to save it: this device, your phone or a security key.',
  and: 'and',
  createPasskey: 'Create a passkey',
  useExistingPasskey: 'Use a passkey I already have',
  notNow: 'Not now',
  passkeyUnsupported:
    "This browser can't create passkeys. Open the portal in an up-to-date browser, or on your phone, and sign in there.",
  passkeyWindowExpired: 'That took a little too long. Sign in again to create the passkey.',

  // WebAuthn ceremony outcomes (client-side).
  passkeyDismissed: 'The passkey prompt was closed. Try again when you are ready.',
  passkeyNotRecognized: "That passkey isn't one this portal knows. Continue with your e-mail to sign in.",
  passkeyGenericError: 'Something went wrong with the passkey. Please try again.',

  // `/login?oidcError=<reason>` redirect reasons from the BFF.
  oidcAccessDenied: 'Sign-in was cancelled.',
  oidcNotProvisioned: 'Your account has no portal access yet. Ask your administrator for an invitation.',
  oidcTenantNotAllowed: 'Your organization is not enabled for this portal. Contact your administrator.',
  oidcRoleNotAssigned:
    'Your account is not assigned to this portal. Ask your administrator to assign you to the application.',
  oidcEmailMissing: 'Your sign-in account has no verified email address. Contact your administrator.',
  oidcEmailNotAllowed:
    "This account's email is not covered by your organization's single sign-on. Contact your administrator.",
  oidcSsoUnknown: 'This sign-in link is no longer valid. Ask your administrator for the current one.',
  oidcExpired: 'The sign-in attempt expired or was tampered with. Please try again.',
  oidcDisabled: 'Single sign-on is not enabled.',
  oidcGenericError: 'Single sign-on failed. Please try again.'
}
